import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { logActivity } from '@/lib/activityLog';
import {
  type SalesSettings,
  DEFAULT_SETTINGS,
  SETTING_LABELS,
  formatSettingValue,
  normalizePaymentMethods,
} from '@/lib/salesSettings';

interface AuditEntry {
  setting_key: string;
  setting_label: string;
  old_value: string;
  new_value: string;
}

function diffSettings(
  before: SalesSettings,
  after: SalesSettings
): AuditEntry[] {
  const keys = Object.keys(SETTING_LABELS);
  const entries: AuditEntry[] = [];

  for (const key of keys) {
    const a = (before as unknown as Record<string, unknown>)[key];
    const b = (after as unknown as Record<string, unknown>)[key];
    const aStr = JSON.stringify(a);
    const bStr = JSON.stringify(b);
    if (aStr !== bStr) {
      entries.push({
        setting_key: key,
        setting_label: SETTING_LABELS[key],
        old_value: formatSettingValue(key, a),
        new_value: formatSettingValue(key, b),
      });
    }
  }
  return entries;
}

export function useSalesSettings() {
  const { user, profile } = useAuth();
  const [settings, setSettings] = useState<SalesSettings | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<SalesSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: selectError } = await supabase
        .from('sales_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (selectError) throw selectError;

      let row = data as SalesSettings | null;

      if (!row) {
        const { data: created, error: insertError } = await supabase
          .from('sales_settings')
          .insert({ id: DEFAULT_SETTINGS.id })
          .select('*')
          .single();
        if (insertError) throw insertError;
        row = created as SalesSettings;
      }

      setSettings({ ...row, payment_methods: normalizePaymentMethods(row.payment_methods) });
      setSavedSnapshot({ ...row, payment_methods: normalizePaymentMethods(row.payment_methods) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!settings || !savedSnapshot) return false;
    return JSON.stringify(settings) !== JSON.stringify(savedSnapshot);
  }, [settings, savedSnapshot]);

  const validate = useCallback((s: SalesSettings): string | null => {
    if (s.tax_percentage < 0 || s.tax_percentage > 100) {
      return 'El porcentaje de impuesto debe estar entre 0 y 100.';
    }
    if (s.discount_max_percentage < 0 || s.discount_max_percentage > 100) {
      return 'El porcentaje máximo sin autorización debe estar entre 0 y 100.';
    }
    if (!s.payment_methods.some((m) => m.enabled)) {
      return 'Debe haber al menos un método de pago activo.';
    }
    if (!s.invoice_prefix.trim()) {
      return 'El prefijo de factura no puede estar vacío.';
    }
    if (s.invoice_digits < 1 || s.invoice_digits > 12) {
      return 'La cantidad de dígitos debe estar entre 1 y 12.';
    }
    if (s.invoice_start_number < 0) {
      return 'El número inicial de factura no puede ser negativo.';
    }
    if (!s.invoice_footer_message.trim()) {
      return 'El mensaje final de la factura no puede estar vacío.';
    }
    return null;
  }, []);

  const writeAudit = useCallback(
    async (entries: AuditEntry[]) => {
      if (entries.length === 0) return;
      const userName = [profile?.first_name, profile?.last_name]
        .filter(Boolean)
        .join(' ') || user?.email || 'Desconocido';

      const rows = entries.map((e) => ({
        user_id: user?.id ?? null,
        user_name: userName,
        setting_key: e.setting_key,
        setting_label: e.setting_label,
        old_value: e.old_value,
        new_value: e.new_value,
      }));

      await supabase.from('settings_audit_log').insert(rows);
    },
    [user, profile]
  );

  const save = useCallback(async (): Promise<boolean> => {
    if (!settings || !savedSnapshot) {
      setError('No hay configuración para guardar.');
      return false;
    }

    const validationError = validate(settings);
    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return false;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: Record<string, unknown> = { ...settings };
      delete payload.created_at;
      payload.updated_at = new Date().toISOString();

      const { error: upsertError } = await supabase
        .from('sales_settings')
        .upsert(payload, { onConflict: 'id' });

      if (upsertError) throw upsertError;

      const changes = diffSettings(savedSnapshot, settings);
      await writeAudit(changes);
      void logActivity({
        module: 'settings',
        action: 'settings_changed',
        description: 'Configuración de venta actualizada',
        targetType: 'settings',
        metadata: { changes: changes.length },
      });

      setSavedSnapshot(settings);
      setSuccess('Configuración guardada correctamente.');
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la configuración.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [settings, savedSnapshot, validate, writeAudit]);

  const restoreDefaults = useCallback(async (): Promise<boolean> => {
    if (!settings) return false;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const defaults: SalesSettings = {
        ...DEFAULT_SETTINGS,
        id: settings.id,
        payment_methods: DEFAULT_SETTINGS.payment_methods.map((m) => ({ ...m })),
      };

      const payload: Record<string, unknown> = { ...defaults };
      delete payload.created_at;
      payload.updated_at = new Date().toISOString();

      const { error: upsertError } = await supabase
        .from('sales_settings')
        .upsert(payload, { onConflict: 'id' });

      if (upsertError) throw upsertError;

      const changes = diffSettings(settings, defaults);
      await writeAudit(changes);
      void logActivity({
        module: 'settings',
        action: 'settings_reset',
        description: 'Configuración de venta restaurada a valores predeterminados',
        targetType: 'settings',
      });

      setSettings(defaults);
      setSavedSnapshot(defaults);
      setSuccess('Configuración restaurada a los valores predeterminados.');
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo restaurar la configuración.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [settings, writeAudit]);

  const patch = useCallback((partial: Partial<SalesSettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  return {
    settings,
    loading,
    saving,
    error,
    success,
    dirty,
    patch,
    save,
    restoreDefaults,
    reload: load,
    clearMessages: () => {
      setError(null);
      setSuccess(null);
    },
  };
}